import React from 'react';
import { useShippingPageContext } from '../providers/ShippingPageProvider';
import CartViewSummaryDE from '../../../../components/CartViewSummaryDE';
import { translate } from '../../../../utils/translator';
import RadioSelect from '../../../../components/RadioSelect/RadioSelect';
import { useCartContext } from '../../../../customHooks/useCart';
import SubmitButton from '../../../../components/SubmitButton/SubmitButton';
import { MainTracker } from '../../../../PixelTrackers/MainTracker';
import AnimatedPopup from '../../../../components/AnimatedPopup/AnimatedPopup';

interface FastReviewUpsellPopUpProps {
  isVisible: boolean;
  onClose: () => void;
}

const FastReviewUpsellPopUp: React.FC<FastReviewUpsellPopUpProps> = ({ isVisible, onClose }) => {
  const {
    isFastReviewUpsellShown,
    setIsExpressPrescriptionReview,
    isExpressPrescriptionReview,
    language,
    selectedFormID,
    isSubmitButtonDisabled
  } = useShippingPageContext();

  const { updateCart, getCart } = useCartContext();
  const cart = getCart();

  // Helper function to track upsale_accepted
  const trackUpsaleAccepted = () => {
    const customerID = window.eventsTracking?.getData?.("customerID") || null;
    const orderId = cart.id || null;
    const fastReviewFeeCents = 500;
    
    MainTracker.track('custom', 'upsale_accepted', {
      user_id: customerID,
      order_id: orderId,
      approval_before: 'standard',
      approval_after: 'fast',
      fee_cents: fastReviewFeeCents,
    });
  };

  // Helper function to track upsale_declined
  const trackUpsaleDeclined = (declineAction: 'close_modal' | 'click_no') => {
    const customerID = window.eventsTracking?.getData?.("customerID") || null;
    const orderId = cart.id || null;
    
    MainTracker.track('custom', 'upsale_declined', {
      user_id: customerID,
      order_id: orderId,
      approval_current: 'standard',
      decline_action: declineAction
    });
  };

  const handleClose = () => {
    trackUpsaleDeclined('close_modal');
    onClose();
  };

  const optionBase =
    'w-full rounded-[6px] overflow-hidden transition cursor-pointer ' +
    'appearance-none outline-none shadow-none ' +
    'focus:outline-none focus-visible:outline-none ' +
    'ring-0 focus:ring-0 focus-visible:ring-0 ' +
    'active:shadow-none focus:shadow-none';

  return (
    <AnimatedPopup
      isVisible={isVisible}
      onClose={handleClose}
      title={translate(language, 'shipping', 'default/get_your_order_faster')}
      containerClassName="md:!max-w-[400px]"
    >
      <div className="w-full">
        {/* ================= FAST OPTION ================= */}
        <button
          type="button"
          onClick={() => {
            setIsExpressPrescriptionReview(true);
            updateCart({ is_express_prescription_review: true });
          }}
          style={{
            border: isExpressPrescriptionReview ? '1px solid #269C6E' : '1px solid #D3D3D3',
            borderRadius: '6px'
          }}
          className={`${optionBase} mb-3 ${
            isExpressPrescriptionReview ? 'bg-[#F7FAEF]' : 'bg-white'
          }`}
        >
          {/* Image */}
          <div
            className={`h-[140px] flex items-center justify-center ${
              isExpressPrescriptionReview ? 'bg-[#F3FBF7]' : 'bg-white'
            }`}
          >
            <div className="relative w-[100px] h-[100px] bg-white rounded-full flex items-center justify-center">
              <img
                src="/delivery_box.png"
                alt="Delivery Box"
                className="max-h-[90px] object-contain pointer-events-none"
              />
              <img
                src="/fast_delivery.png"
                alt="Fast Delivery"
                className="absolute right-[-10px] top-[18px] h-[75px] pointer-events-none"
              />
            </div>
          </div>

          {/* Text */}
          <div className="flex items-center gap-3 px-4 py-3">
            <RadioSelect
              selected={isExpressPrescriptionReview}
              disabled={false}
              backgroundColor="#00C389"
              size={20}
            />
            <p className="text-[14px] text-[#0D2C54] leading-tight text-left">
              {translate(language, 'shipping', 'default/doktor_approve_1_hour_part1')}{' '}
              <span className="font-bold">
                {translate(language, 'shipping', 'default/doktor_approve_1_hour_part2')}
              </span>
            </p>
          </div>
        </button>

        {/* ================= STANDARD OPTION ================= */}
        <button
          type="button"
          onClick={() => {
            setIsExpressPrescriptionReview(false);
            updateCart({ is_express_prescription_review: false });
          }}
          style={{
            border: !isExpressPrescriptionReview ? '1px solid #269C6E' : '1px solid #D3D3D3',
            borderRadius: '6px'
          }}
          className={`${optionBase} mb-5 flex items-center gap-3 px-4 py-3 ${
            !isExpressPrescriptionReview ? 'bg-[#F7FAEF]' : 'bg-white'
          }`}
        >
          <RadioSelect
            selected={!isExpressPrescriptionReview}
            disabled={false}
            backgroundColor="#00C389"
            size={20}
          />
          <p className="text-[14px] text-[#0D2C54] leading-tight text-left">
            {translate(language, 'shipping', 'default/doktor_approve_5_hours_part1')}{' '}
            <span className="font-bold">
              {translate(language, 'shipping', 'default/doktor_approve_5_hours_part2')}
            </span>
          </p>
        </button>

        {/* Cart summary */}
        <div className="mb-5">
          <CartViewSummaryDE isCannabisCatalog idPrefix="fast-review-upsell" />
        </div>

        {/* sticky footer */}
        <div
          className="border-t border-gray-200 bg-white md:mb-0 mb-6"
          style={{
            position: 'sticky',
            bottom: 0,
            left: 0,
            width: '100%',
            zIndex: 20,
          }}
        >
          {/* CTA */}
          <SubmitButton
            id={"fast-review-upsell-submit-button"}
            loading={isSubmitButtonDisabled}
            language={language}
            onSubmit={() => {
              if (isExpressPrescriptionReview) {
                trackUpsaleAccepted();
              } else {
                trackUpsaleDeclined('click_no');
              }
              
              isFastReviewUpsellShown.current = true;
            }}
            formID={selectedFormID}
            placeholder={translate(language, "shipping", "ShippingForm/button_title")}
            className='w-full'
          />
        </div>
      </div>
    </AnimatedPopup>
  );
};

export default FastReviewUpsellPopUp;
